import React, { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { createPortal } from "react-dom";
import {
  ChevronRightIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  HomeIcon,
} from "@heroicons/react/24/outline";
import { getDisciplines } from "../../../api/hse/disciplines";

/**
 * Combined Discipline/Category/Chapter Selector
 * Based on ModernProductSelectorV2 design pattern
 * 
 * @param {Object} props
 * @param {string} props.disciplineId - Selected discipline ID or code
 * @param {string} props.categoryId - Selected category ID or code
 * @param {string} props.chapterNumber - Selected chapter number
 * @param {Function} props.onDisciplineChange - Callback when discipline changes
 * @param {Function} props.onCategoryChange - Callback when category changes
 * @param {Function} props.onChapterChange - Callback when chapter changes
 * @param {boolean} props.required - Whether selection is required
 * @param {number} [props.overlayZIndex=2600] - z-index when portaled above modals (PMDialog uses 2000)
 */
export const DisciplineCategoryChapterSelector = ({
  disciplineId,
  categoryId,
  chapterNumber,
  onDisciplineChange,
  onCategoryChange,
  onChapterChange,
  required = false,
  overlayZIndex = 2600,
}) => {
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedChapters, setExpandedChapters] = useState(new Set());
  const [categoryBreadcrumb, setCategoryBreadcrumb] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSelector, setShowSelector] = useState(false);

  // Fetch disciplines
  useEffect(() => {
    fetchDisciplines();
  }, []);

  const fetchDisciplines = async () => {
    try {
      setLoading(true);
      const response = await getDisciplines();
      const loadedDisciplines = response.company_disciplines || [];
      setDisciplines(loadedDisciplines);
    } catch (err) {
      console.error("Error fetching disciplines:", err);
      setDisciplines([]);
    } finally {
      setLoading(false);
    }
  };

  // Find selected discipline - handle both ID and code matching, and string/number conversion (case-insensitive)
  const selectedDiscipline = useMemo(() => {
    if (!disciplineId) return null;
    if (disciplines.length === 0) return null; // Wait for disciplines to load
    
    const searchId = String(disciplineId || '').toLowerCase().trim();
    
    const found = disciplines.find((d) => {
      const dId = String(d.id || '').toLowerCase().trim();
      const dCode = String(d.code || '').toLowerCase().trim();
      
      // Try exact match first (case-insensitive)
      if (dId === searchId || dCode === searchId) return true;
      
      // Try numeric match
      const dIdNum = parseInt(dId);
      const dCodeNum = parseInt(dCode);
      const searchNum = parseInt(searchId);
      
      if (!isNaN(dIdNum) && !isNaN(searchNum) && dIdNum === searchNum) return true;
      if (!isNaN(dCodeNum) && !isNaN(searchNum) && dCodeNum === searchNum) return true;
      
      return false;
    });
    
    return found || null;
  }, [disciplines, disciplineId]);

  // Find selected category - handle both ID and code matching (case-insensitive)
  const selectedCategory = useMemo(() => {
    if (!categoryId) {
      return null;
    }
    
    const searchId = String(categoryId || '').toLowerCase().trim();

    const matchesCategory = (c) => {
      const cId = String(c.id || '').toLowerCase().trim();
      const cCode = String(c.code || '').toLowerCase().trim();
      
      // Exact match (case-insensitive)
      if (cId === searchId || cCode === searchId) return true;
      
      // Numeric match
      const cIdNum = parseInt(cId);
      const cCodeNum = parseInt(cCode);
      const searchNum = parseInt(searchId);
      
      if (!isNaN(cIdNum) && !isNaN(searchNum) && cIdNum === searchNum) return true;
      if (!isNaN(cCodeNum) && !isNaN(searchNum) && cCodeNum === searchNum) return true;
      
      return false;
    };

    const foundInDiscipline = selectedDiscipline?.categories?.find(matchesCategory);
    if (foundInDiscipline) {
      return foundInDiscipline;
    }

    // Some historical documents have category_id set even if the discipline-category
    // association is missing. Still show the saved category instead of only the discipline.
    for (const discipline of disciplines) {
      const found = discipline.categories?.find(matchesCategory);
      if (found) return found;
    }
    
    return null;
  }, [disciplines, selectedDiscipline, categoryId]);

  // Get selected chapter - handle string/number conversion and recursive search in sub-chapters
  const selectedChapter = useMemo(() => {
    if (!chapterNumber) {
      return null;
    }
    
    // Use category from breadcrumb if available, otherwise use selectedCategory from props
    // But ensure we use the category that actually has chapters loaded
    let categoryToUse = categoryBreadcrumb.length > 0 
      ? categoryBreadcrumb[categoryBreadcrumb.length - 1] 
      : selectedCategory;
    
    // If categoryToUse doesn't have chapters, try to get it from selectedDiscipline
    if (!categoryToUse?.chapters && selectedDiscipline?.categories) {
      const foundCategory = selectedDiscipline.categories.find((c) => {
        const cId = String(c.id || '').toLowerCase().trim();
        const cCode = String(c.code || '').toLowerCase().trim();
        const searchId = String(categoryId || '').toLowerCase().trim();
        return cId === searchId || cCode === searchId || 
               (!isNaN(parseInt(cId)) && !isNaN(parseInt(searchId)) && parseInt(cId) === parseInt(searchId));
      });
      if (foundCategory?.chapters) {
        categoryToUse = foundCategory;
      }
    }
    
    if (!categoryToUse?.chapters) {
      console.log('DisciplineCategoryChapterSelector - selectedChapter - Early return (no chapters):', {
        chapterNumber,
        hasCategoryToUse: !!categoryToUse,
        hasChapters: !!categoryToUse?.chapters,
        chaptersCount: categoryToUse?.chapters?.length,
        breadcrumbLength: categoryBreadcrumb.length,
        selectedCategoryHasChapters: !!selectedCategory?.chapters,
        selectedDisciplineHasCategories: !!selectedDiscipline?.categories
      });
      return null;
    }
    
    const searchNum = String(chapterNumber || '').trim();
    
    console.log('DisciplineCategoryChapterSelector - selectedChapter - Searching:', {
      chapterNumber,
      searchNum,
      categoryToUse: categoryToUse.name,
      categoryToUseId: categoryToUse.id,
      availableChapters: categoryToUse.chapters.map(ch => ({
        number: ch.number,
        title: ch.title,
        hasSubChapters: !!(ch.sub_chapters && ch.sub_chapters.length > 0),
        subChaptersCount: ch.sub_chapters?.length || 0
      }))
    });
    
    // Recursive function to search chapters and sub-chapters
    const findChapter = (chapters, level = 0) => {
      if (!chapters || chapters.length === 0) return null;
      
      for (const ch of chapters) {
        const chNum = String(ch.number || '').trim();
        
        // Exact match
        if (chNum === searchNum) {
          console.log('DisciplineCategoryChapterSelector - selectedChapter - Exact match found:', {
            chapter: ch.title,
            number: ch.number,
            level,
            isSubChapter: level > 0
          });
          return ch;
        }
        
        // Search in sub-chapters recursively
        const nested = ch.sub_chapters || ch.subChapters || [];
        if (nested.length > 0) {
          const found = findChapter(nested, level + 1);
          if (found) return found;
        }
      }
      
      return null;
    };
    
    const found = findChapter(categoryToUse.chapters);
    
    if (!found) {
      console.warn('DisciplineCategoryChapterSelector - selectedChapter - No match found:', {
        chapterNumber,
        searchNum,
        categoryToUse: categoryToUse.name,
        availableChapters: categoryToUse.chapters.map(ch => ({
          number: ch.number,
          title: ch.title,
          subChapters: (ch.sub_chapters || ch.subChapters || []).map(sc => ({ number: sc.number, title: sc.title }))
        }))
      });
    }
    
    return found;
  }, [selectedCategory, chapterNumber, categoryBreadcrumb, selectedDiscipline, categoryId]);

  // Auto-expand and navigate to selected values when disciplines are loaded
  useEffect(() => {
    if (disciplines.length > 0 && selectedDiscipline && selectedCategory) {
      // Expand the selected category
      setExpandedCategories(prev => new Set([...prev, selectedCategory.id]));
      // Set breadcrumb to show selected category - this navigates to the category view
      // This ensures chapters are visible when editing an existing document
      // Always set breadcrumb when we have a selected category (especially when editing existing document)
      if (categoryBreadcrumb.length === 0) {
        setCategoryBreadcrumb([selectedCategory]);
      } else {
        // Update breadcrumb if category changed
        const currentCategory = categoryBreadcrumb[categoryBreadcrumb.length - 1];
        if (!currentCategory || currentCategory.id !== selectedCategory.id) {
          setCategoryBreadcrumb([selectedCategory]);
        }
      }
    }
  }, [disciplines.length, selectedDiscipline, selectedCategory]);

  // Auto-expand selected chapter when category is selected
  useEffect(() => {
    if (selectedCategory && selectedChapter) {
      // Find parent chapter if this is a sub-chapter
      // Sub-chapters have numbers like "2.2" where parent is "2"
      const chapterNumberParts = selectedChapter.number.split('.');
      if (chapterNumberParts.length > 1) {
        // This is a sub-chapter, find and expand parent chapter
        const parentNumber = chapterNumberParts[0];
        
        // Use category from breadcrumb if available, otherwise use selectedCategory
        const categoryToUse = categoryBreadcrumb.length > 0 
          ? categoryBreadcrumb[categoryBreadcrumb.length - 1] 
          : selectedCategory;
        
        // Search for parent chapter in category's chapters
        const findParentChapter = (chapters) => {
          if (!chapters || chapters.length === 0) return null;
          
          for (const ch of chapters) {
            const chNum = String(ch.number || '').trim();
            if (chNum === parentNumber) {
              return ch;
            }
            // Search in sub-chapters recursively
            const nested = ch.sub_chapters || ch.subChapters || [];
            if (nested.length > 0) {
              const found = findParentChapter(nested);
              if (found) return found;
            }
          }
          return null;
        };
        
        if (categoryToUse?.chapters) {
          const parentChapter = findParentChapter(categoryToUse.chapters);
          if (parentChapter) {
            // Expand parent chapter so sub-chapter is visible
            setExpandedChapters(prev => new Set([...prev, parentChapter.number]));
          }
        }
      }
      
      // Expand the chapter if it has sub-chapters
      const selectedNested = selectedChapter.sub_chapters || selectedChapter.subChapters || [];
      if (selectedNested.length > 0) {
        setExpandedChapters(prev => new Set([...prev, selectedChapter.number]));
      }
      // Ensure breadcrumb is set so chapters are visible when editing existing document
      if (categoryBreadcrumb.length === 0 && selectedCategory) {
        setCategoryBreadcrumb([selectedCategory]);
      }
    }
  }, [selectedCategory, selectedChapter, categoryBreadcrumb]);

  // Debug logging to help identify matching issues
  useEffect(() => {
    if (disciplines.length > 0 && disciplineId && categoryId) {
      console.log('DisciplineCategoryChapterSelector Debug:', {
        disciplineId,
        categoryId,
        chapterNumber,
        selectedDiscipline: selectedDiscipline?.name,
        selectedCategory: selectedCategory?.name,
        selectedChapter: selectedChapter?.number,
        disciplineCategories: selectedDiscipline?.categories?.map(c => ({ id: c.id, code: c.code, name: c.name })),
        categoryChapters: selectedCategory?.chapters?.map(ch => ({ number: ch.number, title: ch.title }))
      });
    }
  }, [disciplines.length, disciplineId, categoryId, chapterNumber, selectedDiscipline, selectedCategory, selectedChapter]);

  // Ensure chapter matching works with string comparison
  // The issue might be that chapter.number is stored as a string like "6.1" but we're comparing incorrectly
  const isChapterSelected = useCallback((chapter) => {
    if (!chapterNumber || !chapter) return false;
    const chNum = String(chapter.number || '').trim();
    const searchNum = String(chapterNumber || '').trim();
    
    // Exact match
    if (chNum === searchNum) return true;
    
    return false;
  }, [chapterNumber]);

  // Filter categories/chapters based on search
  const filterItems = useCallback((items, query) => {
    if (!query) return items;
    const lowerQuery = query.toLowerCase();
    return items.filter((item) =>
      item.name?.toLowerCase().includes(lowerQuery) ||
      item.title?.toLowerCase().includes(lowerQuery) ||
      item.number?.toString().includes(lowerQuery)
    );
  }, []);

  // Handle discipline selection
  const handleDisciplineSelect = useCallback((discipline) => {
    onDisciplineChange?.(discipline.id || discipline.code);
    onCategoryChange?.("");
    onChapterChange?.("");
    setCategoryBreadcrumb([]);
    setExpandedCategories(new Set());
    setExpandedChapters(new Set());
  }, [onDisciplineChange, onCategoryChange, onChapterChange]);

  // Handle category navigation
  const handleCategoryNavigate = useCallback((category, event) => {
    event.stopPropagation();
    
    // Build breadcrumb path
    const path = findCategoryPath(selectedDiscipline?.categories || [], category.id);
    const newBreadcrumb = path || [category];
    
    setCategoryBreadcrumb(newBreadcrumb);
    // Send both ID and the full category object for better mapping
    onCategoryChange?.(category.id || category.code, category);
    onChapterChange?.("");
    
    // Expand category
    setExpandedCategories(prev => new Set([...prev, category.id]));
  }, [selectedDiscipline, onCategoryChange, onChapterChange]);

  // Find path to category
  const findCategoryPath = useCallback((catList, targetId, path = []) => {
    for (const cat of catList) {
      const currentPath = [...path, cat];
      if (cat.id === targetId) {
        return currentPath;
      }
      // Categories don't have nested children in this structure
    }
    return null;
  }, []);

  // Handle chapter selection
  const handleChapterSelect = useCallback((chapter, event) => {
    event.stopPropagation();
    onChapterChange?.(chapter.number);
  }, [onChapterChange]);

  // Toggle category expansion
  const toggleCategoryExpansion = useCallback((categoryId, event) => {
    event.stopPropagation();
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  // Toggle chapter expansion
  const toggleChapterExpansion = useCallback((chapterNumber, event) => {
    event.stopPropagation();
    setExpandedChapters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chapterNumber)) {
        newSet.delete(chapterNumber);
      } else {
        newSet.add(chapterNumber);
      }
      return newSet;
    });
  }, []);

  // Render chapter tree
  const renderChapterTree = useCallback((chapters, level = 0) => {
    if (!chapters || chapters.length === 0) return null;

    return chapters.map((chapter) => {
      const nested = chapter.sub_chapters || chapter.subChapters || [];
      const hasSubChapters = nested.length > 0;
      const isExpanded = expandedChapters.has(chapter.number);
      const isSelected = isChapterSelected(chapter);

      return (
        <div key={chapter.number} className="select-none">
          <div
            className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors ${
              isSelected
                ? "bg-blue-100 text-blue-700 font-medium"
                : "hover:bg-gray-100 text-gray-700"
            }`}
            style={{ paddingLeft: `${level * 16 + 8}px` }}
            onClick={(e) => {
              if (hasSubChapters) {
                toggleChapterExpansion(chapter.number, e);
              } else {
                handleChapterSelect(chapter, e);
              }
            }}
          >
            {hasSubChapters && (
              <button
                onClick={(e) => toggleChapterExpansion(chapter.number, e)}
                className="p-0.5 hover:bg-gray-200 rounded flex-shrink-0"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>
            )}
            {!hasSubChapters && <div className="w-5 flex-shrink-0" />}
            <span className="flex-1 text-sm truncate" title={`${chapter.number} - ${chapter.title}`}>
              {chapter.number} - {chapter.title}
            </span>
            {isSelected && (
              <CheckIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
            )}
          </div>
          {isExpanded && hasSubChapters && (
            <div className="mt-1">
              {renderChapterTree(nested, level + 1)}
            </div>
          )}
        </div>
      );
    });
  }, [expandedChapters, chapterNumber, toggleChapterExpansion, handleChapterSelect, isChapterSelected]);

  // Filtered categories
  const filteredCategories = useMemo(() => {
    if (!selectedDiscipline?.categories) return [];
    return filterItems(selectedDiscipline.categories, searchQuery);
  }, [selectedDiscipline, searchQuery, filterItems]);

  // Get display text - memoized to update when selections change
  const displayText = useMemo(() => {
    // If we have IDs but disciplines haven't loaded yet, show loading state
    if (disciplineId && disciplines.length === 0 && loading) {
      return "Laster...";
    }
    
    // Use category from breadcrumb if available, otherwise use selectedCategory from props
    const categoryToDisplay = categoryBreadcrumb.length > 0 
      ? categoryBreadcrumb[categoryBreadcrumb.length - 1] 
      : selectedCategory;
    
    const parts = [];
    if (selectedDiscipline) {
      parts.push(selectedDiscipline.name);
    }
    if (categoryToDisplay) {
      parts.push(categoryToDisplay.name);
    }
    if (selectedChapter) {
      parts.push(`${selectedChapter.number} - ${selectedChapter.title}`);
    }
    
    if (parts.length === 0) {
      return "Velg fag/bransje, kategori og kapittel";
    }
    
    return parts.join(" • ");
  }, [selectedDiscipline, selectedCategory, selectedChapter, categoryBreadcrumb, disciplineId, disciplines.length, loading]);

  const selectorOverlay =
    showSelector && typeof document !== "undefined" ? (
      <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: overlayZIndex }}>
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={() => setShowSelector(false)}
          aria-hidden="true"
        />
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            className="relative w-full max-w-lg bg-white border border-gray-200 rounded-lg shadow-xl max-h-[min(24rem,calc(100dvh-2rem))] overflow-hidden flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label="Velg fag/bransje, kategori og kapittel"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex-shrink-0 p-3 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  Velg fag/bransje, kategori og kapittel
                </h4>
                <button
                  type="button"
                  onClick={() => setShowSelector(false)}
                  className="p-1 rounded-md text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>

              {/* Search */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Søk..."
                  className="block w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Breadcrumb */}
            {categoryBreadcrumb.length > 0 && (
              <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-1 text-xs text-gray-600 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      setCategoryBreadcrumb([]);
                      onCategoryChange?.("");
                      onChapterChange?.("");
                    }}
                    className="flex items-center gap-1 hover:text-blue-600"
                  >
                    <HomeIcon className="h-3 w-3" />
                    <span>Alle kategorier</span>
                  </button>
                  {categoryBreadcrumb.map((category, index) => (
                    <Fragment key={category.id}>
                      <ChevronRightIcon className="h-3 w-3 text-gray-400" />
                      <button
                        type="button"
                        onClick={() => {
                          const newBreadcrumb = categoryBreadcrumb.slice(0, index + 1);
                          const selectedCategoryItem = newBreadcrumb[newBreadcrumb.length - 1];
                          setCategoryBreadcrumb(newBreadcrumb);
                          onCategoryChange?.(
                            selectedCategoryItem.id || selectedCategoryItem.code,
                            selectedCategoryItem
                          );
                        }}
                        className="hover:text-blue-600 truncate max-w-[100px]"
                        title={category.name}
                      >
                        {category.name}
                      </button>
                    </Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-2">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                </div>
              ) : !selectedDiscipline ? (
                <div className="space-y-1">
                  {filterItems(disciplines, searchQuery).map((discipline) => (
                    <div
                      key={discipline.id || discipline.code}
                      className="flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-gray-100 text-gray-700"
                      onClick={() => handleDisciplineSelect(discipline)}
                    >
                      <span className="flex-1 text-sm">{discipline.name}</span>
                      <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              ) : categoryBreadcrumb.length > 0 ? (
                <div className="space-y-1">
                  {(() => {
                    const categoryToShow =
                      categoryBreadcrumb[categoryBreadcrumb.length - 1] || selectedCategory;
                    return categoryToShow?.chapters && categoryToShow.chapters.length > 0 ? (
                      renderChapterTree(categoryToShow.chapters)
                    ) : (
                      <div className="text-sm text-gray-500 text-center py-4">
                        Ingen kapitler tilgjengelig
                      </div>
                    );
                  })()}
                </div>
              ) : !selectedCategory ? (
                <div className="space-y-1">
                  {filteredCategories.map((category) => {
                    const categoryIdStr = String(categoryId || "").toLowerCase().trim();
                    const catId = String(category.id || "").toLowerCase().trim();
                    const catCode = String(category.code || "").toLowerCase().trim();
                    const isSelected = catId === categoryIdStr || catCode === categoryIdStr;

                    return (
                      <div key={category.id || category.code} className="select-none">
                        <div
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors ${
                            isSelected
                              ? "bg-blue-100 text-blue-700 font-medium"
                              : "hover:bg-gray-100 text-gray-700"
                          }`}
                          onClick={(e) => handleCategoryNavigate(category, e)}
                        >
                          <span className="flex-1 text-sm truncate" title={category.name}>
                            {category.name}
                          </span>
                          {isSelected && (
                            <CheckIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          )}
                          <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-1">
                  {selectedCategory.chapters && selectedCategory.chapters.length > 0 ? (
                    renderChapterTree(selectedCategory.chapters)
                  ) : (
                    <div className="text-sm text-gray-500 text-center py-4">
                      Ingen kapitler tilgjengelig
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="flex-shrink-0 p-2 border-t border-gray-200 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  if (selectedCategory) {
                    setCategoryBreadcrumb([]);
                    onCategoryChange?.("");
                    onChapterChange?.("");
                  } else if (selectedDiscipline) {
                    onDisciplineChange?.("");
                    onCategoryChange?.("");
                    onChapterChange?.("");
                  }
                }}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                {selectedCategory
                  ? "Tilbake til kategorier"
                  : selectedDiscipline
                    ? "Tilbake til fag/bransje"
                    : ""}
              </button>
              <button
                type="button"
                onClick={() => setShowSelector(false)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Ferdig
              </button>
            </div>
          </div>
        </div>
      </div>
    ) : null;

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setShowSelector(!showSelector)}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-left text-sm ${
          required && !disciplineId ? "border-red-300" : ""
        } focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
      >
        <span className={`truncate ${disciplineId ? "text-gray-900" : "text-gray-500"}`}>
          {displayText}
        </span>
        <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
      </button>

      {selectorOverlay && createPortal(selectorOverlay, document.body)}
    </div>
  );
};

